/**
 * Até quando a página renderizada no servidor vale — o `valido-ate` que a Lambda de render grava e
 * o porteiro confere a cada visita (SSR do cardápio, Etapa 2.2).
 *
 * O HTML depende da HORA em um ponto: item de cardápio (produto, categoria, sabor) ligado a um
 * modelo de horário (`templateID` → `menu.hoursTemplates`) só aparece dentro das faixas dele.
 * Numa loja que liga o próprio horário de funcionamento aos itens (a okpizzaria, por exemplo), o
 * cardápio muda quando a loja abre e quando fecha. Sem prazo, a página das 17h continuaria sendo
 * servida às 19h, com a lista errada, até o cardápio ser regravado.
 *
 * A regra de "aberto" é a do `HourTemplate` do cardapnator (faixas do dia e da véspera, fim
 * inclusivo, faixa que cruza a meia-noite), no relógio de parede do fuso da loja — o mesmo que o
 * app passa às bibliotecas com `setTimeZone`. O prazo é o primeiro instante em que algum modelo
 * USADO por algum item muda de estado. Borda que não muda estado (24 h, faixas encostadas) não conta.
 *
 * O "aberto/fechado" do cabeçalho não entra: ele é escondido no HTML e só aparece depois da
 * hidratação (`HeaderPage.posHidratacao`).
 */

/** Fuso quando o cadastro não tem: o do backend, e o da maioria das lojas. */
export const FUSO_PADRAO = 'America/Sao_Paulo';

export function fusoDaLoja(info: { timezone?: string | null } | null | undefined): string {
    return (info && info.timezone) || FUSO_PADRAO;
}

const DIA = 86_400_000;
const DIAS_DA_SEMANA = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
/** Fuso inválido: prazo curto, para a página não durar com a hora de outro lugar. */
const PRAZO_SEM_FUSO_MS = 15 * 60_000;

/**
 * Relógio de parede do fuso como milissegundos "UTC falsos": os campos de `Date.UTC` são a hora
 * local da loja. Aritmética de dias e horas fica exata, sem depender do fuso da máquina.
 */
function parede(instante: number, fuso: string): number {
    const p: Record<string, string> = {};
    // `hourCycle` não está no `lib` do TypeScript do app (ES2018), mas todo navegador e o Node 20
    // o aceitam; sem ele, alguns motores escrevem a meia-noite como "24" (daí o `% 24` abaixo).
    const opcoes: any = {
        timeZone: fuso, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    };
    new Intl.DateTimeFormat('en-US', opcoes).formatToParts(new Date(instante)).forEach((x) => { p[x.type] = x.value; });
    return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
}

/** Instante real de uma hora de parede do fuso (duas iterações resolvem horário de verão). */
function instanteDaParede(paredeMs: number, fuso: string, perto: number): number {
    // A parede não tem milissegundos: o deslocamento sai de um instante no segundo cheio.
    const base = perto - (perto % 1000);
    let t = paredeMs - (parede(base, fuso) - base);
    t = paredeMs - (parede(t, fuso) - t);
    return t;
}

function segundosDoDia(hora: string): number {
    const [h, m, s] = String(hora || '0:0:0').split(':').map((x) => +x || 0);
    return (h * 3600 + m * 60 + s) * 1000;
}

type Faixa = { firstTime: string; secondTime: string; isClosed?: any };
type Modelo = { id: any; available?: any; hours?: Record<string, Faixa[]> };

/** As faixas de um dia (meia-noite em parede) com início e fim já em parede. */
function faixasDoDia(modelo: Modelo, meiaNoite: number): Array<{ inicio: number; fim: number; fechada: boolean }> {
    const dia = DIAS_DA_SEMANA[new Date(meiaNoite).getUTCDay()];
    return ((modelo.hours && modelo.hours[dia]) || []).map((f) => {
        const inicio = meiaNoite + segundosDoDia(f.firstTime);
        let fim = meiaNoite + segundosDoDia(f.secondTime);
        if (inicio >= fim) fim += DIA;
        return { inicio, fim, fechada: !!f.isClosed };
    });
}

/** `HourTemplate.checkIfIsOpenByHours`: faixa de hoje ou da véspera que contém o instante. */
export function abertoNoModelo(modelo: Modelo, paredeMs: number): boolean {
    const hoje = paredeMs - (paredeMs % DIA);
    for (const meiaNoite of [hoje, hoje - DIA]) {
        for (const f of faixasDoDia(modelo, meiaNoite)) {
            if (paredeMs >= f.inicio && paredeMs <= f.fim && !f.fechada) return true;
        }
    }
    return false;
}

/** Ids de modelo que algum item do cardápio usa (`templateID` em qualquer profundidade). */
function modelosUsados(menu: any): Set<string> {
    const ids = new Set<string>();
    const pilha: any[] = [menu];
    while (pilha.length) {
        const o = pilha.pop();
        if (Array.isArray(o)) { for (const x of o) if (x && typeof x === 'object') pilha.push(x); continue; }
        for (const chave of Object.keys(o)) {
            const v = o[chave];
            if (chave === 'templateID') { if (v !== null && v !== undefined && v !== '' && v !== 0) ids.add(String(v)); continue; }
            if (chave === 'hoursTemplates') continue;
            if (v && typeof v === 'object') pilha.push(v);
        }
    }
    return ids;
}

/**
 * Epoch (ms) a partir do qual a página renderizada em `agora` deixa de valer, ou `null` quando
 * nada nela depende da hora.
 */
export function validadeDaPagina(cardapio: any, agora: number): number | null {
    const menu = cardapio && cardapio.menu;
    const todos: Modelo[] = (menu && Array.isArray(menu.hoursTemplates)) ? menu.hoursTemplates : [];
    if (!todos.length) return null;
    const usados = modelosUsados(menu);
    // `available` falso: o item fica indisponível a qualquer hora (`unavailableByTemplate`).
    const modelos = todos.filter((t) => usados.has(String(t.id)) && t.available && t.hours);
    if (!modelos.length) return null;

    const fuso = fusoDaLoja(cardapio.info);
    let agoraParede: number;
    try { agoraParede = parede(agora, fuso); } catch (e) { return agora + PRAZO_SEM_FUSO_MS; }

    // Candidatas: todo início e todo fim+1s de faixa entre a véspera e daqui a 8 dias. O padrão é
    // semanal: sem mudança em 8 dias, não muda nunca.
    const hoje = agoraParede - (agoraParede % DIA);
    const bordas: number[] = [];
    for (let d = -1; d <= 8; d++) {
        for (const m of modelos) {
            for (const f of faixasDoDia(m, hoje + d * DIA)) bordas.push(f.inicio, f.fim + 1000);
        }
    }
    const estadoAgora = modelos.map((m) => abertoNoModelo(m, agoraParede));
    const futuras = [...new Set(bordas.filter((b) => b > agoraParede))].sort((a, b) => a - b);
    for (const b of futuras) {
        if (modelos.some((m, i) => abertoNoModelo(m, b) !== estadoAgora[i])) return instanteDaParede(b, fuso, agora);
    }
    return null;
}
